import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import { getDisplayDateFormat } from "helpers/DatesHelper";

import { useFormatMenuItem } from "hooks/useFormatMenuItem";
import {
    AgrometeorologyRoutes,
    InfoBox,
    Overlay,
    PanelMenuItem,
    SearchableDropdown,
    useTitle,
} from "modules/common";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AppRoutes } from "routing";
import { ForecastCityDto } from "services/iot/models";
import { useCitiesQueries } from "services/iot/recordsHooks";

const getMenuItems = (): PanelMenuItem[] => [
    {
        label: "Danas",
        link: AgrometeorologyRoutes.WeatherToday,
    },
    {
        label: "Prognoza",
        link: AgrometeorologyRoutes.CityWeatherForecast,
        fullScreen: true,
    },
    {
        label: "Agrometeorološka prognoza",
        link: AgrometeorologyRoutes.AgrometeorologyForecast,
    },
    {
        label: "Karta kretanja oblaka",
        link: AgrometeorologyRoutes.CloudMovement,
        fullScreen: true,
    },
    {
        label: "Karta vjetra i temperature",
        link: AgrometeorologyRoutes.WindAndTemperatureMap,
    },
];

export const Agrometeorology: React.FC = () => {
    useTitle("Agrometeorologija - eSavjetnik");

    const { pathname } = useLocation();
    const history = useHistory();

    const menuItems = useMemo(() => getMenuItems(), []);
    const [city, setCity] = useState<ForecastCityDto>();
    const [menuItem, setMenuItem] = useState<PanelMenuItem>(menuItems[0]);

    const { cities } = useCitiesQueries();

    useEffect(() => {
        const routeMap: Record<string, string> = {
            [AgrometeorologyRoutes.WeatherToday]: AgrometeorologyRoutes.WeatherToday,
            [AgrometeorologyRoutes.CityWeatherForecast]: AgrometeorologyRoutes.CityWeatherForecast,
            [AgrometeorologyRoutes.AgrometeorologyForecast]:
                AgrometeorologyRoutes.AgrometeorologyForecast,
            [AgrometeorologyRoutes.CloudMovement]: AgrometeorologyRoutes.CloudMovement,
            [AgrometeorologyRoutes.WindAndTemperatureMap]:
                AgrometeorologyRoutes.WindAndTemperatureMap,
        };

        const targetRoute = routeMap[pathname];

        if (targetRoute) {
            const foundMenuItem = menuItems.find(mi => mi.link === targetRoute);
            if (foundMenuItem) {
                setMenuItem(foundMenuItem);
            }
        } else if (pathname === AppRoutes.Agrometeorology) {
            const defaultMenuItem = menuItems.find(
                mi => mi.link === AgrometeorologyRoutes.WeatherToday
            );
            if (defaultMenuItem) {
                setMenuItem(defaultMenuItem);
            }
            history.push(AgrometeorologyRoutes.WeatherToday);
        }
    }, [pathname, menuItems, history]);

    const titleBarProps =
        menuItem?.link === AgrometeorologyRoutes.AgrometeorologyForecast
            ? {
                  aboveMenu: true,
                  text: "IZVOR PODATAKA: DHMZ",
                  fullScreenButton: false,
              }
            : undefined;

    const search = useMemo(() => {
        if (!menuItem) {
            return null;
        }

        const { link } = menuItem;
        // WeatherToday && CityWeatherForecast need to show the search box
        if (
            link !== AgrometeorologyRoutes.WeatherToday &&
            link !== AgrometeorologyRoutes.CityWeatherForecast
        ) {
            // WindAndTemperatureMap shows some info instead of search, for others, use null
            if (link !== AgrometeorologyRoutes.WindAndTemperatureMap) return null;

            return <InfoBox left={getDisplayDateFormat(new Date())} right="Izvor podataka: DHMZ" />;
        } else {
            return (
                <SearchableDropdown
                    items={cities}
                    noOptionsMessage={() => "Nema pronađenih gradova"}
                    labelKey="localizedName"
                    onSelect={setCity}
                    placeholder="Pretraži gradove"
                    customIcons={{
                        searchIcon: <SearchIcon width={14} height={14} />,
                    }}
                    isClearable
                />
            );
        }
    }, [cities, menuItem]);

    const formatMenuItem = useFormatMenuItem(menuItem?.label);

    return (
        <Overlay
            key={`${pathname}-${menuItem?.link}`}
            containerClass={formatMenuItem}
            backButton
            search={search}
            fullScreenInitially={menuItem?.fullScreen ?? false}
            view={{
                title: "Agrometeorologija",
                source: "agrometeorologija",
                parameters: { tab: menuItem?.link, city: city?.name },
                dynamicHeight: true,
            }}
            menuItems={menuItems}
            onMenuItemChanged={mi => {
                history.push(mi.link);
            }}
            selectedMenuItem={menuItem}
            titleBarProps={titleBarProps}
        />
    );
};
